import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { OverflownText } from 'components/OverflownText'
import { CompanyDistributor } from 'models/companies'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { PiLock, PiMagnifyingGlass, PiPlus } from 'react-icons/pi'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import FbButton from '../../../components/FbUI/FbButton'
import { BootstrapInput } from '../../../components/FormUtils/NumericInput'
import { Modal } from '../../../components/UI/Modal/Modal'
import { handleFormError } from '../../../utils/handleFormError'

const defaultValuesDistributor = {
  name: '',
  description: '',
  url: '',
}

export function DistributorTable() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedDistributorID, setSelectedDistributorID] = useState<number>()
  const [searchQuery, setSearchQuery] = useState('')

  const {
    data: distributorsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['distributors'],
    queryFn: async () => {
      return await api.getDistributors({
        params: {
          is_default: false,
        },
      })
    },
  })

  const sortedData = distributorsData?.sort(
    // Sort by is_default first, then by name
    (a, b) =>
      (b.is_default ? 1 : -1) - (a.is_default ? 1 : -1) ||
      a.name.localeCompare(b.name)
  )

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !sortedData) return sortedData
    const q = searchQuery.toLowerCase()
    return sortedData.filter(
      (item) =>
        item.name?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    )
  }, [sortedData, searchQuery])

  const {
    control: control,
    handleSubmit: handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
  } = useForm({
    defaultValues: defaultValuesDistributor,
    mode: 'onSubmit',
  })

  function openModal() {
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    reset(defaultValuesDistributor)
    setSelectedDistributorID(undefined)
  }

  const onSubmitCreate = handleSubmit(async (values) => {
    const [err] = await to(
      api.postDistributor({
        name: values.name,
        description: values.description,
        url: values.url,
      })
    )
    if (err) handleFormError(err, setError)

    await refetch()
    queryClient.invalidateQueries({ queryKey: ['distributors'] })
    toast.success('Distributor created successfully')
    hideModal()
  })

  const onSubmitUpdate = handleSubmit(async (values) => {
    if (!selectedDistributorID) return

    const [err] = await to(
      api.updateDistributor(selectedDistributorID, {
        name: values.name,
        description: values.description,
        url: values.url,
      })
    )
    if (err) handleFormError(err, setError)

    await refetch()
    queryClient.invalidateQueries({ queryKey: ['distributors'] })
    toast.success('Distributor updated successfully')
    hideModal()
  })

  async function handleRowDelete(index: number) {
    const id = data?.[index]?.id
    if (!id) return

    if (
      await dialogConfirm(
        'Are you sure you want to delete this distributor?',
        'Delete Distributor'
      )
    ) {
      await api.deleteDistributor(id)
      void refetch()
      queryClient.invalidateQueries({ queryKey: ['distributors'] })
      toast.success('Distributor deleted successfully')
    }
  }

  function handleRowEdit(index: number) {
    setShowModal(true)
    setSelectedDistributorID(data?.[index]?.id)
    if (data) reset(data?.[index])
  }

  const columns = useMemo<ColumnDef<CompanyDistributor, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 240,
        meta: { isFirstColumn: true },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 500,
        cell: (info) => {
          const value = info.getValue()
          return <OverflownText maxLines={2}>{value}</OverflownText>
        },
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => {
          if (row.original.is_default)
            return (
              <div
                className="flex w-full justify-center text-gray-500"
                title="Default distributor — cannot be edited"
              >
                <PiLock size={16} />
              </div>
            )
          return (
            <ActionsDropdown
              className="!w-full justify-center"
              actions={[
                createEditAction(() => handleRowEdit(row.index)),
                createDeleteAction(() => handleRowDelete(row.index)),
              ]}
            />
          )
        },
      },
    ],
    [data]
  )

  if (isLoading || !sortedData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center justify-between gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search distributors..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
                <FbButton
                  variant="primary"
                  size="sm"
                  onClick={() => openModal()}
                  className="!bg-brand-bright !text-white hover:!bg-brand-active shrink-0"
                >
                  <PiPlus size={16} />
                  Create Distributor
                </FbButton>
              </div>
            }
            height="100%"
            stickyLastColumn
            data={data ?? []}
            columns={columns}
          />
        </DataTableContainer>
      </div>
      <Modal
        open={showModal}
        title={
          selectedDistributorID ? 'Edit Distributor' : 'Create New Distributor'
        }
        description={
          selectedDistributorID ? (
            <>
              Edit your distributor to reflect your current process and
              performance.
            </>
          ) : (
            <>
              Create a distributor to reflect your current process and its
              performance.
            </>
          )
        }
        onOpenChange={(open) => !open && hideModal()}
        loading={isSubmitting}
        blockAccept={!isValid}
        onAccept={selectedDistributorID ? onSubmitUpdate : onSubmitCreate}
        acceptButtonText={selectedDistributorID ? 'Update' : 'Create'}
      >
        <BootstrapInput
          name="name"
          control={control}
          type="text"
          label="Name"
          placeholder="Distributor Name"
        />
        <BootstrapInput
          name="url"
          control={control}
          type="text"
          label="URL"
          placeholder="URL"
        />
        <TextareaInput
          name="description"
          control={control}
          type="textarea"
          label="Description"
          placeholder="Description"
        />
      </Modal>
    </TableLinkStyleProvider>
  )
}
